import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, shareReplay, tap } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class FormioCacheInterceptor implements HttpInterceptor {
    public cacheStorage = new Map<string, Observable<HttpEvent<any>>>();

    /**
     * Intercepts HTTP requests to cache responses when X-Designer-Cache header is present.
     * If the request has the cache header, it will return cached responses if available.
     * Otherwise, it will cache new responses for future requests.
     *
     * @param req The HTTP request to intercept.
     * @param next The next handler in the HTTP pipeline.
     * @returns An observable of the HTTP event, which is either the cached response or a new response.
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if this request should be cached (has X-Designer-Cache header)
        const shouldCache = req.headers.has('X-Designer-Cache');

        if (shouldCache) {
            const cacheKey = this.generateCacheKey(req);

            // Attempt to retrieve the response from cache
            const cachedResponse = this.cacheStorage.get(cacheKey);
            if (cachedResponse) {
                return cachedResponse;
            }

            // If not cached, handle the request and cache the response
            const shared = next.handle(req).pipe(
                tap((event: HttpEvent<any>) => {
                    // Check if the event is a valid HttpResponse
                    if (event instanceof HttpResponse) {
                        const responseBody = event?.body as any;
                        // Remove from cache if the response status is not 200
                        if (!responseBody?.isSuccess) {
                            this.cacheStorage.delete(cacheKey);
                        }
                    }
                }),
                shareReplay(1) // Share the response among subscribers
            );

            // Store the new response in cache
            this.cacheStorage.set(cacheKey, shared);
            return shared;
        }

        // If caching is not requested, proceed with the request as normal
        return next.handle(req);
    }

    /**
     * Generates a unique cache key for a given HTTP request.
     * The cache key is determined based on the URL and the request body.
     * If the request body is null or undefined, the key is only based on the URL.
     *
     * @param req the HTTP request for which to generate the cache key
     * @returns the generated cache key as a string
     */
    private generateCacheKey(req: HttpRequest<any>): string {
        const baseKey = `${req.method}:${req.url}`;

        // If the request has a body, include a hash of the payload in the cache key
        // This ensures that different payloads are cached separately
        if (req.body) {
            const payloadHash = this.hashPayload(req.body);
            return `${baseKey}:${payloadHash}`;
        }

        // If the request has no body, the cache key is only based on the URL
        return baseKey;
    }

    /**
     * Calculates a hash from the given payload.
     * The hash is a MD5 string calculated from the concatenated and sorted values of the payload.
     * If the payload is null or undefined, the string 'empty' is returned.
     *
     * @param payload the payload to calculate the hash from
     * @returns the hash as a string
     */
    private hashPayload(payload: any): string {
        if (!payload) return 'empty';

        const values: string[] = [];
        this.extractObjectValues(payload, values);
        values.sort();

        return this.createHash(values.join('|'));
    }

    /**
     * Extracts the values from the given object and adds them to the given array.
     * The object can be a nested object or an array.
     *
     * @param obj the object to extract the values from
     * @param values the array to add the values to
     */
    private extractObjectValues(obj: any, values: string[]): void {
        if (obj === null || obj === undefined) {
            // If the object is null or undefined, add 'null' to the values array
            values.push('null');
            return;
        }

        if (typeof obj !== 'object') {
            // If the object is not an object, add its string value to the values array
            values.push(String(obj));
            return;
        }

        if (Array.isArray(obj)) {
            // If the object is an array, iterate over its elements and recursively call this method
            obj.forEach(item => this.extractObjectValues(item, values));
            return;
        }

        // Iterate over the object's properties and recursively call this method
        Object.keys(obj).forEach(key => {
            if (key !== 'hashValue' && obj.hasOwnProperty(key)) {
                // Add the key to the values array
                values.push(key);
                // Recursively call this method for each property
                this.extractObjectValues(obj[key], values);
            }
        });
    }

    /**
     * Creates a hash value for the given string.
     *
     * This function is a simple implementation of the djb2 hash algorithm.
     * It takes a string as input and returns a hash value as a string.
     *
     * @param str the string to hash
     * @returns a hash value as a string
     */
    private createHash(str: string): string {
        let hash = 0;
        // Iterate over each character in the string
        for (let i = 0; i < str.length; i++) {
            // Calculate the hash value
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            // Make sure the hash value is positive
            hash = hash & hash;
        }
        // Return the hash value as a string
        return Math.abs(hash).toString(36);
    }
}